import * as z from 'zod';
import { EntityId, LocalizedString, Quantity, Ören } from '../base-schemas';
import { isoDateTimeToAppTime } from '../base-codecs';
export const ReservationSelection = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('none')
    }).strict(),
    z.object({
        type: z.literal('date'),
        date: z.iso.date()
    }).strict(),
    z.object({
        type: z.literal('date_range'),
        start_date: z.iso.date(),
        end_date: z.iso.date()
    }).strict(),
    z.object({
        type: z.literal('time_interval'),
        start_date_time: z.iso.datetime({ local: true }),
        duration_minutes: z.int().positive().optional()
    }).strict(),
    z.object({
        type: z.literal('week'),
        start_date: z.iso.date()
    }).strict(),
    z.object({
        type: z.literal('month'),
        start_date: z.iso.date()
    }).strict(),
    z.object({
        type: z.literal('year'),
        year: z.int().min(2000).max(2100)
    }).strict(),
    z.object({
        type: z.literal('fixed_period'),
        start_date_time: z.iso.datetime({ local: true }),
        end_date_time: z.iso.datetime({ local: true })
    }).strict()
]);
export const AdminValidateCartItemInput = z.object({
    product_id: EntityId,
    reservation_option_id: EntityId,
    preferred_reservable_ids: z.array(EntityId).nullish(),
    quantity: Quantity,
    cart_item_id: z.string().min(1).max(64),
    selection: ReservationSelection,
    price: Ören.optional(),
    price_mode: z.enum(['catalog', 'per_step', 'total']).optional(),
    tier_prices: z.array(z.object({ tier_id: EntityId, price: Ören }).strict()).optional()
}).strict();
// Only the option_tiers engine reaches this endpoint. The server's other engine,
// 'manual', is produced exclusively by admin item writes — CartItemRules prohibits
// is_manually_scheduled on every validate-cart route — so it is not modelled here.
const PricingBreakdown = z.object({
    version: z.literal(2),
    engine: z.literal('option_tiers'),
    booking_family: z.enum(['unscheduled', 'timed', 'day_based', 'fixed_period']),
    reservation_option: z.object({ id: EntityId, label: LocalizedString }).strict(),
    tiers: z.array(z.object({
        tier_id: EntityId,
        label: LocalizedString,
        unit_kind: z.string(),
        unit_size: z.int().positive(),
        applied_price: Ören,
        quantity: z.int().positive(),
        subtotal: Ören
    }).strict()),
    duration_minutes: z.int().positive().nullable(),
    unit_price: Ören,
    total_price: Ören,
    price_mode: z.enum(['catalog', 'per_step', 'total']),
    backfilled: z.boolean(),
    // The catalog price before any override. Differs from unit_price/total_price
    // only when price_mode is 'per_step' or 'total'.
    catalog_unit_price: Ören,
    catalog_total_price: Ören,
    steps: z.int().nonnegative()
}).strict();
export const CartItemFailureCode = z.enum([
    'unknown_product',
    'invalid_selection',
    'reservation_option_required',
    'reservation_option_unavailable',
    'product_not_configured',
    'missing_catalog_price',
    'incomplete_override_pricing_schedule',
    'invalid_price_mode',
    'total_price_required'
]);
const ValidatedCartItemBase = z.object({
    product_id: EntityId,
    reservation_option_id: EntityId.optional(),
    preferred_reservable_ids: z.array(EntityId).nullish(),
    quantity: Quantity,
    product_name: LocalizedString,
    cart_item_id: z.string().min(1).max(64),
    delivery_date_time: isoDateTimeToAppTime.optional(),
    return_date_time: isoDateTimeToAppTime.optional(),
    available_reservable_ids: z.array(EntityId).optional()
}).strict();
export const ValidatedCartItem = z.discriminatedUnion('is_invalid', [
    ValidatedCartItemBase.extend({
        is_invalid: z.literal(false),
        unit_price: Ören,
        total_price: Ören,
        pricing_breakdown: PricingBreakdown.optional()
    }).strict(),
    ValidatedCartItemBase.extend({
        is_invalid: z.literal(true),
        invalid_reasons: z.array(LocalizedString),
        invalid_reason_codes: z.array(CartItemFailureCode).optional(),
        max_available_quantity: Quantity.optional(),
        is_invalid_skippable: z.boolean().optional(),
        unit_price: Ören.optional(),
        total_price: Ören.optional()
    }).strict()
]);
export const ValidateCartResponse = z.object({
    is_valid: z.boolean(),
    total_amount: Ören,
    all_failures_skippable: z.boolean().nullable().optional(),
    cart_items: z.array(ValidatedCartItem)
}).strict();
